import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { ArrowLeft, CreditCard, FileText, Info, RefreshCw } from 'lucide-react';

import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import { formatDateOnly } from '../utils/dateOnly';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED' | string;

type InvoiceRow = {
  id: string;
  business_id: string;
  bill_to: string;
  customer_email?: string | null;
  invoice_date: string;
  due_date: string;
  status: InvoiceStatus;
  total: string | number;
  deposit?: string | number;
  due?: string | number;
  business?: any;
};

const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v: any): string => safeNum(v).toFixed(2);

const toInvoiceLabel = (id: string): string => {
  const short = (id || '').split('-')[0]?.toUpperCase() || '—';
  return `INV-${short}`;
};

const fmtDate = (v: any): string => formatDateOnly(v, 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const getBusinessName = (row: InvoiceRow | null): string => {
  const b: any = (row as any)?.business;
  const name = String(b?.name ?? b?.business_name ?? '').trim();
  return name || 'Business';
};

function CheckoutForm(props: { invoiceId: string }) {
  const { invoiceId } = props;
  const navigate = useNavigate();

  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (!stripe || !elements) return;

      try {
        setSubmitting(true);
        const returnUrl = `${window.location.origin}/dashboard/business/invoices/${encodeURIComponent(invoiceId)}/charge/complete`;
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required',
        });

        if (result.error) {
          setError(result.error.message || 'Payment failed. Please try again.');
          return;
        }

        const pi = result.paymentIntent;
        if (pi?.id) {
          try {
            await apiService.syncInvoicePaymentIntent(invoiceId, pi.id);
          } catch {
            // Non-fatal; completion page can show success from Stripe
          }
          navigate(`/dashboard/business/invoices/${encodeURIComponent(invoiceId)}/charge/complete?payment_intent=${encodeURIComponent(pi.id)}`);
        } else {
          navigate(`/dashboard/business/invoices/${encodeURIComponent(invoiceId)}`);
        }
      } catch (err: any) {
        setError(err?.message || 'Payment failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [elements, invoiceId, navigate, stripe]
  );

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <PaymentElement />
        </div>
        {error ? <div className="text-sm text-red-600">{error}</div> : null}
        <button type="submit" className="aacore-btn2-primary w-full justify-center gap-2" disabled={!stripe || !elements || submitting}>
          <CreditCard className="w-4 h-4" />
          {submitting ? 'Processing…' : 'Charge card'}
        </button>
        <div className="text-xs text-gray-500">Payments are processed securely by Stripe. Do not refresh the page while processing.</div>
      </form>
      {submitting ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" aria-modal="true" role="dialog" aria-live="polite" aria-busy="true">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 max-w-sm w-full text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B4778] mx-auto mb-4" aria-hidden />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Processing payment</h2>
            <p className="text-sm text-gray-600 mb-0">Please wait. Do not refresh or close this page.</p>
          </div>
        </div>
      ) : null}
    </>
  );
}

const BusinessChargeInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const formStateFromLocation = (location.state as any)?.formState;
  const formState =
    formStateFromLocation ??
    (invoiceId && (() => {
      try {
        const key = `createInvoiceFormState_${invoiceId}`;
        const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
        return raw ? JSON.parse(raw) : undefined;
      } catch {
        return undefined;
      }
    })());

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<InvoiceRow | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  const businessName = getBusinessName(invoice);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiService.getStripeConfig();
        setStripePromise(loadStripe(cfg.publishableKey));
      } catch (e: any) {
        console.error('Failed to load Stripe config', e);
        setError(e?.response?.data?.error || e?.message || 'Stripe is not configured');
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    try {
      setLoading(true);
      setError(null);
      const inv = await apiService.getInvoice(invoiceId);
      setInvoice(inv as any);
      const pi = await apiService.createBusinessInvoicePaymentIntent(invoiceId);
      setClientSecret(pi.clientSecret);
      setPaymentIntentId(pi.paymentIntentId);
    } catch (e: any) {
      console.error('Failed to initialize business charge', e);
      setInvoice(null);
      setClientSecret(null);
      setPaymentIntentId(null);
      setError(e?.response?.data?.error || 'Failed to start payment');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!invoice) return;
    const s = String(invoice.status || '').toUpperCase();
    if (s === 'PAID') toast('This invoice is already paid.');
  }, [invoice]);

  const goBack = () => {
    if (invoiceId) {
      navigate(`/dashboard/business/invoices/${invoiceId}`);
      return;
    }
    navigate('/dashboard/business?section=invoices');
  };

  if (!invoiceId) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h1>
            <p className="text-gray-600 mb-6">Missing invoice ID.</p>
            <button onClick={() => navigate('/dashboard/business?section=invoices')} className="aacore-btn2-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Invoices
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !invoice || !stripePromise || !clientSecret) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar />
        <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr]">
          {shouldShowBusinessDashboard(user!) && (
            <BusinessDashboardSidebar
              isSidebarOpen={isSidebarOpen}
              isSidebarCollapsed={isSidebarCollapsed}
              activeSection="invoices"
              onClose={() => setIsSidebarOpen(false)}
              onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              unreadNotificationCount={0}
            />
          )}
          <div className="flex-1 p-4 md:p-8 flex items-center justify-center">
            <div className="text-center max-w-md">
              <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Start Payment</h1>
              <p className="text-gray-600 mb-6">{error || 'Payment is unavailable'}</p>
              <div className="flex gap-2 justify-center flex-wrap">
                <button onClick={() => load()} className="aacore-btn2-primary inline-flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Try again
                </button>
                <button onClick={goBack} className="aacore-btn2-outline inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to invoice details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isPayable = ['DRAFT', 'SENT'].includes(String(invoice.status || '').toUpperCase());

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr]">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="invoices"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            unreadNotificationCount={0}
          />
        )}
        <div className="flex-1 p-4 md:p-8">
          <button onClick={goBack} className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to invoice details
          </button>

          <div className="max-w-2xl">
            <h1 className="text-2xl font-semibold text-gray-900 mb-6">Charge customer</h1>

            <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">CHARGE INVOICE</div>
                  <h2 className="text-xl font-bold mb-0">{toInvoiceLabel(invoice.id)}</h2>
                  <p className="text-gray-600 text-sm mb-0">
                    {businessName} • {invoice.bill_to} • Due {fmtDate(invoice.due_date)}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 mb-1">AMOUNT DUE</div>
                  <div className="text-2xl font-bold">
                    ${formatMoney(safeNum(invoice.due) >= 0 ? invoice.due : Math.max(0, safeNum(invoice.total) - safeNum(invoice.deposit)))}
                  </div>
                </div>
              </div>
              <div className="mt-3 p-3 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-600 flex gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                <div>
                  You are charging the customer’s card for this invoice. A copy of the paid invoice will be sent to the receipt email once payment succeeds.
                </div>
              </div>
            </div>

            {!isPayable ? (
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <div className="text-gray-900 font-semibold mb-1">This invoice can’t be charged</div>
                <div className="text-gray-600 text-sm">Status is {String(invoice.status || '—')}. Only draft or sent invoices can be charged here.</div>
                <button onClick={goBack} className="mt-3 aacore-btn2-outline inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to invoice details
                </button>
              </div>
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                <div className="hidden" aria-hidden="true">
                  {paymentIntentId || ''}
                </div>
                <CheckoutForm invoiceId={invoiceId} />
              </Elements>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessChargeInvoicePage;
